import React, { useState } from "react";
import { IoClose } from "react-icons/io5";

const AddInstallmentModal = ({ isOpen, onClose, onSave, isSaving }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    explanation: "",
    quantity: "",
    rate: "",
    unit: "",
    amount: ""
  });
  const [error, setError] = useState("");

  React.useEffect(() => {
    if (formData.quantity && formData.rate) {
      setFormData(prev => ({
        ...prev,
        amount: (Number(prev.quantity) * Number(prev.rate)).toString()
      }));
    }
  }, [formData.quantity, formData.rate]);

  React.useEffect(() => {
    if (isOpen) {
      setFormData({
        date: new Date().toISOString().split("T")[0],
        explanation: "",
        quantity: "",
        rate: "",
        unit: "",
        amount: ""
      });
      setError("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (!formData.explanation.trim() || !formData.amount || formData.amount <= 0) {
      setError("Please provide a valid explanation and amount.");
      return;
    }
    setError("");
    onSave({
      date: formData.date,
      explanation: formData.explanation,
      amount: Number(formData.amount),
      quantity: formData.quantity ? Number(formData.quantity) : undefined,
      rate: formData.rate ? Number(formData.rate) : undefined,
      unit: formData.unit ? formData.unit : undefined
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-navy-800">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-navy-700 dark:text-white">
            Add Installment
          </h3>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-navy-700"
          >
            <IoClose className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-4 space-y-4">
          {error && (
            <p className="text-sm text-red-500">{error}</p>
          )}
          
          <div>
            <label className="text-sm font-medium text-navy-700 dark:text-white">Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 p-3 text-sm outline-none dark:border-white/10 dark:text-white"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-navy-700 dark:text-white">Particular</label>
            <textarea
              value={formData.explanation}
              onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
              className="mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-3 text-sm outline-none dark:border-white/10 dark:text-white min-h-[80px]"
              placeholder="E.g. First Milestone Payment"
            />
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium text-navy-700 dark:text-white">Quantity</label>
              <input
                type="number"
                min="0"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 p-3 text-sm outline-none dark:border-white/10 dark:text-white"
                placeholder="1"
              />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium text-navy-700 dark:text-white">Unit</label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 p-3 text-sm outline-none dark:border-white/10 dark:text-white"
                placeholder="e.g. kg, L, tin"
              />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium text-navy-700 dark:text-white">Rate (₹)</label>
              <input
                type="number"
                min="0"
                value={formData.rate}
                onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 p-3 text-sm outline-none dark:border-white/10 dark:text-white"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-navy-700 dark:text-white">Amount (₹)</label>
            <input
              type="number"
              min="0"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="mt-2 flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 p-3 text-sm outline-none dark:border-white/10 dark:text-white bg-gray-50 dark:bg-navy-900"
              placeholder="0.00"
              readOnly={!!(formData.quantity && formData.rate)}
            />
          </div>
        </div>

        <div className="mt-4 p-4 rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/30">
          <p className="text-xs text-orange-800 dark:text-orange-300 font-medium leading-relaxed">
            <span className="font-bold">⚠️ Warning:</span> Once this installment is saved, it cannot be edited or deleted to maintain permanent financial records.
          </p>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-xl px-5 py-2.5 text-sm font-medium text-navy-700 hover:bg-gray-100 dark:text-white dark:hover:bg-white/5"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-50 dark:bg-brand-400 dark:hover:bg-brand-300"
          >
            {isSaving ? "Saving..." : "Save Installment"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddInstallmentModal;
